import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Trash2, Building2, User, Mail, Phone,
  DollarSign, Clock, Target, CheckCircle2, Circle,
  ChevronDown, ChevronRight, Plus, Save, AlertTriangle, Sprout,
  ArrowRight, MessageSquare, FileText, Lightbulb,
  Heart, Users, Puzzle, Eye, Leaf, TreePine
} from 'lucide-react';
import { GROWTH_PATH_STEPS, SHOOT_STATUSES, type GrowthPathStep } from '../config/growthPath';
import { API_URL } from '../config/api';

interface Shoot {
  id: string;
  title: string;
  company: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  source: string;
  status: string;
  industry: string;
  companySize: string;
  urgency: string;
  budget?: number;
  timeline?: string;
  priority: number;
  estimatedValue: number;
  createdAt: string;
  updatedAt: string;
  painPoints?: any[];
  currentWorkflows?: any[];
  discoverySessions?: any[];
  salesProcessData?: any;
}

interface GrowthPathStepData {
  completed: boolean;
  completedAt?: string;
  notes?: string;
  data?: Record<string, any>;
}

async function fetchShoot(id: string): Promise<Shoot> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/api/opportunities/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to fetch shoot');
  return res.json();
}

async function updateShoot(id: string, data: Partial<Shoot>): Promise<Shoot> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/api/opportunities/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Failed to update shoot');
  return res.json();
}

function getStepIcon(stepId: string, size = 5) {
  const icons: Record<string, React.ReactNode> = {
    preparation: <Leaf className={`w-${size} h-${size}`} />,
    introduction: <Users className={`w-${size} h-${size}`} />,
    discovery: <Puzzle className={`w-${size} h-${size}`} />,
    summary: <Eye className={`w-${size} h-${size}`} />,
    proposal: <FileText className={`w-${size} h-${size}`} />,
    closing: <Sprout className={`w-${size} h-${size}`} />,
    followup: <TreePine className={`w-${size} h-${size}`} />,
  };
  return icons[stepId] || <Circle className={`w-${size} h-${size}`} />;
}

function GrowthPathTimeline({ 
  currentStep, 
  stepData, 
  onStepClick 
}: { 
  currentStep: number; 
  stepData: Record<string, GrowthPathStepData>;
  onStepClick: (step: number) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <span className="text-xl">🌱</span> Growth Path Progress
      </h3>
      <div className="relative">
        {/* Connection Line */}
        <div className="absolute left-5 top-8 bottom-8 w-0.5 bg-gray-200"></div>
        
        <div className="space-y-4">
          {GROWTH_PATH_STEPS.map((step, index) => {
            const isCompleted = stepData[step.id]?.completed || currentStep > step.step;
            const isCurrent = currentStep === step.step;
            const isPending = currentStep < step.step;
            
            return (
              <div 
                key={step.id}
                onClick={() => onStepClick(step.step)}
                className={`relative flex items-start gap-4 p-3 rounded-lg cursor-pointer transition-all ${
                  isCurrent ? 'bg-green-50 border border-green-200' :
                  isCompleted ? 'bg-gray-50 hover:bg-gray-100' :
                  'hover:bg-gray-50'
                }`}
              >
                {/* Step Indicator */}
                <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted ? 'bg-green-500 text-white' :
                  isCurrent ? 'bg-green-100 text-green-600 ring-2 ring-green-500' :
                  'bg-gray-100 text-gray-400'
                }`}>
                  {isCompleted ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{step.step}</span>
                  )}
                </div>
                
                {/* Step Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{step.icon}</span>
                    <h4 className={`font-medium ${
                      isCompleted ? 'text-gray-600' :
                      isCurrent ? 'text-green-700' :
                      'text-gray-400'
                    }`}>
                      {step.cyvineName}
                    </h4>
                    {isCurrent && (
                      <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>
                  {stepData[step.id]?.completedAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      Completed {new Date(stepData[step.id].completedAt!).toLocaleDateString()}
                    </p>
                  )}
                </div>
                
                {/* Time Estimate */}
                <div className="flex-shrink-0 text-right">
                  <div className={`text-xs ${isCurrent ? 'text-green-600' : 'text-gray-400'}`}>
                    ~{step.timeEstimate}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepWorkspace({
  step,
  shoot,
  stepData,
  onSaveStepData,
  onCompleteStep
}: {
  step: GrowthPathStep;
  shoot: Shoot;
  stepData: GrowthPathStepData;
  onSaveStepData: (data: Record<string, any>) => void;
  onCompleteStep: () => void;
}) {
  const [notes, setNotes] = useState(stepData.notes || '');
  const [expandedSection, setExpandedSection] = useState<string | null>('questions');
  
  const renderStepContent = () => {
    const stepId = step.id as string;
    if (stepId === 'preparation') {
      return <PreparationStep shoot={shoot} stepData={stepData} onSave={onSaveStepData} />;
    } else if (stepId === 'introduction') {
      return <IntroductionStep shoot={shoot} stepData={stepData} onSave={onSaveStepData} />;
    } else if (stepId === 'discovery') {
      return <DiscoveryStep shoot={shoot} stepData={stepData} onSave={onSaveStepData} />;
    } else if (stepId === 'summary') {
      return <SummaryStep shoot={shoot} stepData={stepData} onSave={onSaveStepData} />;
    } else if (stepId === 'phase1') {
      return <ProposalStep shoot={shoot} stepData={stepData} onSave={onSaveStepData} />;
    } else if (stepId === 'closing') {
      return <ClosingStep shoot={shoot} stepData={stepData} onSave={onSaveStepData} />;
    } else if (stepId === 'followup') {
      return <FollowupStep shoot={shoot} stepData={stepData} onSave={onSaveStepData} />;
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Step Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">{step.icon}</span>
          <div>
            <div className="text-green-100 text-sm">Step {step.step} of 7</div>
            <h2 className="text-xl font-semibold">{step.cyvineName}</h2>
          </div>
        </div>
        <p className="text-green-100">{step.description}</p>
      </div>
      
      {/* Servant Mindset */}
      <div className="bg-green-50 border-b border-green-100 px-6 py-4">
        <div className="flex items-start gap-3">
          <Heart className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-green-800 text-sm">Servant Mindset</h4>
            <p className="text-green-700 text-sm mt-1">{step.servantMindset}</p>
          </div>
        </div>
      </div>

      {/* Key Questions */}
      {'questions' in step && step.questions && (
        <div className="p-6 border-b border-gray-200">
          <button
            onClick={() => setExpandedSection(expandedSection === 'questions' ? null : 'questions')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              Key Questions to Explore
            </h3>
            {expandedSection === 'questions' ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSection === 'questions' && (
            <ul className="mt-4 space-y-3">
              {(step.questions as readonly string[]).map((question: string, idx: number) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">{question}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Expected Outputs */}
      {'outputs' in step && step.outputs && (
        <div className="p-6 border-b border-gray-200">
          <button
            onClick={() => setExpandedSection(expandedSection === 'outputs' ? null : 'outputs')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Target className="w-4 h-4 text-gray-500" />
              Expected Outputs
            </h3>
            {expandedSection === 'outputs' ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSection === 'outputs' && (
            <ul className="mt-4 space-y-2">
              {(step.outputs as readonly string[]).map((output: string, idx: number) => (
                <li key={idx} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  {output}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Step-Specific Content */}
      <div className="p-6 border-b border-gray-200">
        {renderStepContent()}
      </div>

      {/* Notes Section */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          Notes & Observations
        </h3>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Capture your notes, observations, and insights from this step..."
          className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
        />
      </div>

      {/* Actions */}
      <div className="p-6 bg-gray-50 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          <Clock className="w-4 h-4 inline mr-1" />
          Estimated time: {step.timeEstimate}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onSaveStepData({ notes })}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Progress
          </button>
          <button
            onClick={onCompleteStep}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            Complete Step
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Step 1: Preparation (Root & Soil Check)
function PreparationStep({ shoot, stepData, onSave }: { shoot: Shoot; stepData: GrowthPathStepData; onSave: (data: any) => void }) {
  const [research, setResearch] = useState(stepData.data?.research || {
    companyWebsite: '',
    recentNews: '',
    linkedInInsights: '',
    industryContext: '',
    potentialChallenges: '',
    connectionPoints: ''
  });

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
        <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
          <Lightbulb className="w-4 h-4" />
          Quick Tips
        </h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• Research the company's website, LinkedIn, and recent news</li>
          <li>• Understand their industry challenges and opportunities</li>
          <li>• Look for connection points (mutual connections, shared interests)</li>
          <li>• Review any previous interactions in CyvineOS</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Website Notes
          </label>
          <textarea
            value={research.companyWebsite}
            onChange={e => setResearch({ ...research, companyWebsite: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What did you learn from their website?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recent News / Press
          </label>
          <textarea
            value={research.recentNews}
            onChange={e => setResearch({ ...research, recentNews: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Any relevant news about the company?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            LinkedIn Insights
          </label>
          <textarea
            value={research.linkedInInsights}
            onChange={e => setResearch({ ...research, linkedInInsights: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Contact's background, company posts, etc."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Industry Context
          </label>
          <textarea
            value={research.industryContext}
            onChange={e => setResearch({ ...research, industryContext: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What's happening in their industry?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Potential Challenges
          </label>
          <textarea
            value={research.potentialChallenges}
            onChange={e => setResearch({ ...research, potentialChallenges: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What problems might they be facing?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Connection Points
          </label>
          <textarea
            value={research.connectionPoints}
            onChange={e => setResearch({ ...research, connectionPoints: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Mutual connections, shared interests, etc."
          />
        </div>
      </div>

      <button
        onClick={() => onSave({ research })}
        className="text-green-600 hover:text-green-700 text-sm font-medium"
      >
        Save Research
      </button>
    </div>
  );
}

// Step 2: Introduction (First Touch & Trust)
function IntroductionStep({ shoot, stepData, onSave }: { shoot: Shoot; stepData: GrowthPathStepData; onSave: (data: any) => void }) {
  const [intro, setIntro] = useState(stepData.data?.intro || {
    openingRapport: '',
    theirStory: '',
    trustMoments: '',
    scheduledDiscovery: false,
    discoveryDate: '',
    discoveryTime: ''
  });

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
          <Heart className="w-4 h-4" />
          Remember
        </h4>
        <p className="text-sm text-blue-700">
          This is about connection, not selling. Ask about their story, their role, and what matters to them.
          Build genuine rapport before discussing business.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Opening Rapport - How did the conversation begin?
          </label>
          <textarea
            value={intro.openingRapport}
            onChange={e => setIntro({ ...intro, openingRapport: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Small talk, common ground discovered, their mood/energy..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Their Story - What did you learn about them personally?
          </label>
          <textarea
            value={intro.theirStory}
            onChange={e => setIntro({ ...intro, theirStory: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Background, career journey, what they care about..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Trust Moments - Any breakthroughs or connection points?
          </label>
          <textarea
            value={intro.trustMoments}
            onChange={e => setIntro({ ...intro, trustMoments: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Shared experiences, values, or interests that built trust..."
          />
        </div>

        <div className="border-t border-gray-200 pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={intro.scheduledDiscovery}
              onChange={e => setIntro({ ...intro, scheduledDiscovery: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm font-medium text-gray-700">Discovery session scheduled</span>
          </label>
          
          {intro.scheduledDiscovery && (
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date</label>
                <input
                  type="date"
                  value={intro.discoveryDate}
                  onChange={e => setIntro({ ...intro, discoveryDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Time</label>
                <input
                  type="time"
                  value={intro.discoveryTime}
                  onChange={e => setIntro({ ...intro, discoveryTime: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={() => onSave({ intro })}
        className="text-green-600 hover:text-green-700 text-sm font-medium"
      >
        Save Introduction Notes
      </button>
    </div>
  );
}

// Step 3: Discovery (Knot-Finding in the Vine)
function DiscoveryStep({ shoot, stepData, onSave }: { shoot: Shoot; stepData: GrowthPathStepData; onSave: (data: any) => void }) {
  const [discovery, setDiscovery] = useState(stepData.data?.discovery || {
    painPoints: [],
    currentWorkflows: [],
    desiredOutcomes: '',
    stakeholders: '',
    timeline: '',
    budget: '',
    decisionProcess: '',
    successMetrics: ''
  });
  const [newPainPoint, setNewPainPoint] = useState('');
  const [newWorkflow, setNewWorkflow] = useState('');

  const addPainPoint = () => {
    if (newPainPoint.trim()) {
      setDiscovery({ ...discovery, painPoints: [...discovery.painPoints, newPainPoint.trim()] });
      setNewPainPoint('');
    }
  };

  const addWorkflow = () => {
    if (newWorkflow.trim()) {
      setDiscovery({ ...discovery, currentWorkflows: [...discovery.currentWorkflows, newWorkflow.trim()] });
      setNewWorkflow('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
        <h4 className="font-medium text-purple-800 flex items-center gap-2 mb-2">
          <Puzzle className="w-4 h-4" />
          Discovery Goals
        </h4>
        <ul className="text-sm text-purple-700 space-y-1">
          <li>• Uncover the real pain points and their business impact</li>
          <li>• Understand current workflows and systems</li>
          <li>• Identify all stakeholders in the decision</li>
          <li>• Define success metrics they care about</li>
        </ul>
      </div>

      {/* Pain Points */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pain Points & Challenges
        </label>
        <div className="space-y-2 mb-2">
          {discovery.painPoints.map((point: string, idx: number) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="flex-1 text-sm">{point}</span>
              <button
                onClick={() => setDiscovery({ 
                  ...discovery, 
                  painPoints: discovery.painPoints.filter((_: any, i: number) => i !== idx) 
                })}
                className="text-red-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newPainPoint}
            onChange={e => setNewPainPoint(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addPainPoint()}
            placeholder="Add a pain point..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
          <button
            onClick={addPainPoint}
            className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Current Workflows */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Current Workflows & Systems
        </label>
        <div className="space-y-2 mb-2">
          {discovery.currentWorkflows.map((workflow: string, idx: number) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="flex-1 text-sm">{workflow}</span>
              <button
                onClick={() => setDiscovery({ 
                  ...discovery, 
                  currentWorkflows: discovery.currentWorkflows.filter((_: any, i: number) => i !== idx) 
                })}
                className="text-blue-400 hover:text-blue-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newWorkflow}
            onChange={e => setNewWorkflow(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addWorkflow()}
            placeholder="Add a current workflow or system..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
          <button
            onClick={addWorkflow}
            className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Desired Outcomes
          </label>
          <textarea
            value={discovery.desiredOutcomes}
            onChange={e => setDiscovery({ ...discovery, desiredOutcomes: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What does success look like for them?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key Stakeholders
          </label>
          <textarea
            value={discovery.stakeholders}
            onChange={e => setDiscovery({ ...discovery, stakeholders: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Who else is involved in this decision?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Timeline & Urgency
          </label>
          <textarea
            value={discovery.timeline}
            onChange={e => setDiscovery({ ...discovery, timeline: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="When do they need a solution?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Budget Context
          </label>
          <textarea
            value={discovery.budget}
            onChange={e => setDiscovery({ ...discovery, budget: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Budget range, approval process, fiscal year timing..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Decision Process
          </label>
          <textarea
            value={discovery.decisionProcess}
            onChange={e => setDiscovery({ ...discovery, decisionProcess: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="How do they make decisions like this?"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Success Metrics
          </label>
          <textarea
            value={discovery.successMetrics}
            onChange={e => setDiscovery({ ...discovery, successMetrics: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="How will they measure success?"
          />
        </div>
      </div>

      <button
        onClick={() => onSave({ discovery })}
        className="text-green-600 hover:text-green-700 text-sm font-medium"
      >
        Save Discovery Notes
      </button>
    </div>
  );
}

// Step 4: Summary (Mirror Back)
function SummaryStep({ shoot, stepData, onSave }: { shoot: Shoot; stepData: GrowthPathStepData; onSave: (data: any) => void }) {
  const [summary, setSummary] = useState(stepData.data?.summary || {
    painPointSummary: '',
    goalSummary: '',
    constraintSummary: '',
    clientConfirmed: false,
    adjustments: ''
  });

  return (
    <div className="space-y-6">
      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
        <h4 className="font-medium text-indigo-800 flex items-center gap-2 mb-2">
          <Eye className="w-4 h-4" />
          Mirror Back
        </h4>
        <p className="text-sm text-indigo-700">
          Summarize what you've heard and get their confirmation. This builds trust and ensures
          you truly understand their needs before proposing solutions.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pain Points Summary
          </label>
          <textarea
            value={summary.painPointSummary}
            onChange={e => setSummary({ ...summary, painPointSummary: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What I'm hearing is that your main challenges are..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Goals Summary
          </label>
          <textarea
            value={summary.goalSummary}
            onChange={e => setSummary({ ...summary, goalSummary: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="And what you're hoping to achieve is..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Constraints Summary
          </label>
          <textarea
            value={summary.constraintSummary}
            onChange={e => setSummary({ ...summary, constraintSummary: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Taking into account your timeline and budget constraints..."
          />
        </div>

        <div className="border-t border-gray-200 pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={summary.clientConfirmed}
              onChange={e => setSummary({ ...summary, clientConfirmed: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm font-medium text-gray-700">Client confirmed summary is accurate</span>
          </label>
        </div>

        {summary.clientConfirmed && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Any adjustments or additions they made?
            </label>
            <textarea
              value={summary.adjustments}
              onChange={e => setSummary({ ...summary, adjustments: e.target.value })}
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
              placeholder="They clarified that..."
            />
          </div>
        )}
      </div>

      <button
        onClick={() => onSave({ summary })}
        className="text-green-600 hover:text-green-700 text-sm font-medium"
      >
        Save Summary
      </button>
    </div>
  );
}

// Step 5: Proposal (Phase 1 Branch Plan)
function ProposalStep({ shoot, stepData, onSave }: { shoot: Shoot; stepData: GrowthPathStepData; onSave: (data: any) => void }) {
  const [proposal, setProposal] = useState(stepData.data?.proposal || {
    scopeDescription: '',
    deliverables: [],
    timeline: '',
    investment: '',
    nextPhasePreview: '',
    riskMitigation: ''
  });
  const [newDeliverable, setNewDeliverable] = useState('');

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setProposal({ ...proposal, deliverables: [...proposal.deliverables, newDeliverable.trim()] });
      setNewDeliverable('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
        <h4 className="font-medium text-orange-800 flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4" />
          Phase 1 Branch Approach
        </h4>
        <p className="text-sm text-orange-700">
          Start with a focused, lower-risk Phase 1 project that delivers quick value.
          This builds trust and proves capability before larger engagements.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phase 1 Scope Description
          </label>
          <textarea
            value={proposal.scopeDescription}
            onChange={e => setProposal({ ...proposal, scopeDescription: e.target.value })}
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Describe the focused Phase 1 project that addresses their most urgent need..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Key Deliverables
          </label>
          <div className="space-y-2 mb-2">
            {proposal.deliverables.map((deliverable: string, idx: number) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="flex-1 text-sm">{deliverable}</span>
                <button
                  onClick={() => setProposal({ 
                    ...proposal, 
                    deliverables: proposal.deliverables.filter((_: any, i: number) => i !== idx) 
                  })}
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newDeliverable}
              onChange={e => setNewDeliverable(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addDeliverable()}
              placeholder="Add a deliverable..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <button
              onClick={addDeliverable}
              className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeline
            </label>
            <input
              type="text"
              value={proposal.timeline}
              onChange={e => setProposal({ ...proposal, timeline: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="e.g., 4-6 weeks"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Investment
            </label>
            <input
              type="text"
              value={proposal.investment}
              onChange={e => setProposal({ ...proposal, investment: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="e.g., $15,000 - $20,000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Vision for Future Phases
          </label>
          <textarea
            value={proposal.nextPhasePreview}
            onChange={e => setProposal({ ...proposal, nextPhasePreview: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Preview of potential Phase 2, 3 work based on Phase 1 success..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Risk Mitigation
          </label>
          <textarea
            value={proposal.riskMitigation}
            onChange={e => setProposal({ ...proposal, riskMitigation: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="How Phase 1 reduces risk for both parties..."
          />
        </div>
      </div>

      <button
        onClick={() => onSave({ proposal })}
        className="text-green-600 hover:text-green-700 text-sm font-medium"
      >
        Save Proposal Details
      </button>
    </div>
  );
}

// Step 6: Closing (Planting the Branch)
function ClosingStep({ shoot, stepData, onSave }: { shoot: Shoot; stepData: GrowthPathStepData; onSave: (data: any) => void }) {
  const [closing, setClosing] = useState(stepData.data?.closing || {
    proposalSent: false,
    proposalSentDate: '',
    clientFeedback: '',
    objections: '',
    objectionsResolved: '',
    agreementReached: false,
    agreementDate: '',
    contractNotes: ''
  });

  return (
    <div className="space-y-6">
      <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
        <h4 className="font-medium text-pink-800 flex items-center gap-2 mb-2">
          <Sprout className="w-4 h-4" />
          Planting the Branch
        </h4>
        <p className="text-sm text-pink-700">
          Guide them gently to a decision. Address any concerns with care and patience.
          The goal is a mutually beneficial partnership, not a pressured sale.
        </p>
      </div>

      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={closing.proposalSent}
              onChange={e => setClosing({ ...closing, proposalSent: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm font-medium text-gray-700">Proposal sent to client</span>
          </label>
          {closing.proposalSent && (
            <div className="mt-3">
              <label className="block text-sm text-gray-600 mb-1">Date sent</label>
              <input
                type="date"
                value={closing.proposalSentDate}
                onChange={e => setClosing({ ...closing, proposalSentDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Client Feedback on Proposal
          </label>
          <textarea
            value={closing.clientFeedback}
            onChange={e => setClosing({ ...closing, clientFeedback: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What was their initial reaction? Any questions?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Objections or Concerns Raised
          </label>
          <textarea
            value={closing.objections}
            onChange={e => setClosing({ ...closing, objections: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What concerns did they express?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            How Were Objections Addressed?
          </label>
          <textarea
            value={closing.objectionsResolved}
            onChange={e => setClosing({ ...closing, objectionsResolved: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="How did you help them work through their concerns?"
          />
        </div>

        <div className="border border-green-200 rounded-lg p-4 bg-green-50">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={closing.agreementReached}
              onChange={e => setClosing({ ...closing, agreementReached: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="font-medium text-green-800">🎉 Agreement reached!</span>
          </label>
          {closing.agreementReached && (
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-sm text-green-700 mb-1">Agreement date</label>
                <input
                  type="date"
                  value={closing.agreementDate}
                  onChange={e => setClosing({ ...closing, agreementDate: e.target.value })}
                  className="px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm text-green-700 mb-1">Contract/Agreement Notes</label>
                <textarea
                  value={closing.contractNotes}
                  onChange={e => setClosing({ ...closing, contractNotes: e.target.value })}
                  className="w-full h-20 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                  placeholder="Contract details, PO number, etc."
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={() => onSave({ closing })}
        className="text-green-600 hover:text-green-700 text-sm font-medium"
      >
        Save Closing Progress
      </button>
    </div>
  );
}

// Step 7: Follow-up (Ongoing Tending)
function FollowupStep({ shoot, stepData, onSave }: { shoot: Shoot; stepData: GrowthPathStepData; onSave: (data: any) => void }) {
  const [followup, setFollowup] = useState(stepData.data?.followup || {
    kickoffScheduled: false,
    kickoffDate: '',
    handoffNotes: '',
    introductionsMade: '',
    successCheckins: '',
    nextSteps: ''
  });

  return (
    <div className="space-y-6">
      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
        <h4 className="font-medium text-emerald-800 flex items-center gap-2 mb-2">
          <TreePine className="w-4 h-4" />
          Ongoing Tending
        </h4>
        <p className="text-sm text-emerald-700">
          The relationship doesn't end at the sale. Set up your new Vine for success
          with proper handoffs and continued care.
        </p>
      </div>

      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={followup.kickoffScheduled}
              onChange={e => setFollowup({ ...followup, kickoffScheduled: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm font-medium text-gray-700">Kickoff meeting scheduled</span>
          </label>
          {followup.kickoffScheduled && (
            <div className="mt-3">
              <label className="block text-sm text-gray-600 mb-1">Kickoff date</label>
              <input
                type="date"
                value={followup.kickoffDate}
                onChange={e => setFollowup({ ...followup, kickoffDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Handoff Notes for Branch Steward
          </label>
          <textarea
            value={followup.handoffNotes}
            onChange={e => setFollowup({ ...followup, handoffNotes: e.target.value })}
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Important context for the project team - client preferences, key stakeholders, success metrics, any special considerations..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Introductions Made
          </label>
          <textarea
            value={followup.introductionsMade}
            onChange={e => setFollowup({ ...followup, introductionsMade: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="Who from our team has been introduced to the client?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Success Check-ins Planned
          </label>
          <textarea
            value={followup.successCheckins}
            onChange={e => setFollowup({ ...followup, successCheckins: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="When will you check in to ensure client satisfaction?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expansion/Next Phase Opportunities
          </label>
          <textarea
            value={followup.nextSteps}
            onChange={e => setFollowup({ ...followup, nextSteps: e.target.value })}
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            placeholder="What future opportunities exist with this Vine?"
          />
        </div>
      </div>

      <button
        onClick={() => onSave({ followup })}
        className="text-green-600 hover:text-green-700 text-sm font-medium"
      >
        Save Follow-up Notes
      </button>
    </div>
  );
}

export default function ShootDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [activeStep, setActiveStep] = useState(1);
  const [stepData, setStepData] = useState<Record<string, GrowthPathStepData>>({});

  const { data: shoot, isLoading, error } = useQuery({
    queryKey: ['shoot', id],
    queryFn: () => fetchShoot(id!),
    enabled: !!id
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Shoot>) => updateShoot(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shoot', id] });
      queryClient.invalidateQueries({ queryKey: ['shoots'] });
    }
  });

  // Initialize step data from shoot
  useEffect(() => {
    if (shoot?.salesProcessData) {
      setStepData(shoot.salesProcessData);
    }
  }, [shoot]);

  // Determine current step based on status
  const getCurrentStep = () => {
    if (!shoot) return 1;
    const statusSteps: Record<string, number> = {
      'new': 1,
      'discovery_scheduled': 2,
      'in_discovery': 3,
      'proposal_pending': 5,
      'proposal_sent': 6,
      'negotiating': 6,
      'won': 7,
      'lost': 7,
      'paused': activeStep,
    };
    return statusSteps[shoot.status] || 1;
  };

  const handleSaveStepData = (stepId: string, data: Record<string, any>) => {
    const newStepData = {
      ...stepData,
      [stepId]: {
        ...stepData[stepId],
        data: { ...stepData[stepId]?.data, ...data }
      }
    };
    setStepData(newStepData);
    
    // Save to server
    updateMutation.mutate({
      salesProcessData: newStepData
    });
  };

  const handleCompleteStep = (stepNumber: number) => {
    const step = GROWTH_PATH_STEPS.find(s => s.step === stepNumber);
    if (!step) return;

    const newStepData = {
      ...stepData,
      [step.id]: {
        ...stepData[step.id],
        completed: true,
        completedAt: new Date().toISOString()
      }
    };
    setStepData(newStepData);

    // Update status based on step completion
    let newStatus = shoot?.status;
    if (stepNumber === 1) newStatus = 'new';
    if (stepNumber === 2) newStatus = 'discovery_scheduled';
    if (stepNumber === 3) newStatus = 'in_discovery';
    if (stepNumber === 4) newStatus = 'proposal_pending';
    if (stepNumber === 5) newStatus = 'proposal_sent';
    if (stepNumber === 6) newStatus = 'negotiating';
    if (stepNumber === 7) newStatus = 'won';

    updateMutation.mutate({
      status: newStatus,
      salesProcessData: newStepData
    });

    // Move to next step
    if (stepNumber < 7) {
      setActiveStep(stepNumber + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !shoot) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Shoot not found</h2>
        <p className="text-gray-500 mt-2">This opportunity may have been removed.</p>
        <Link to="/shoots" className="text-green-600 hover:text-green-700 mt-4 inline-block">
          ← Back to Shoots
        </Link>
      </div>
    );
  }

  const currentStep = getCurrentStep();
  const activeStepConfig = GROWTH_PATH_STEPS.find(s => s.step === activeStep) || GROWTH_PATH_STEPS[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link 
            to="/shoots" 
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Shoots
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
            <Sprout className="w-7 h-7 text-green-600" />
            {shoot.title}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              {shoot.company}
            </span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {shoot.contactName}
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              ${(shoot.estimatedValue || 0).toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {SHOOT_STATUSES.find(s => s.value === shoot.status) && (
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              shoot.status === 'won' ? 'bg-green-100 text-green-700' :
              shoot.status === 'lost' ? 'bg-red-100 text-red-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              {SHOOT_STATUSES.find(s => s.value === shoot.status)?.label}
            </span>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-1">
          <GrowthPathTimeline 
            currentStep={currentStep}
            stepData={stepData}
            onStepClick={setActiveStep}
          />
          
          {/* Contact Info Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mt-6">
            <h3 className="font-medium text-gray-900 mb-3">Contact Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span>{shoot.contactName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <a href={`mailto:${shoot.contactEmail}`} className="text-green-600 hover:underline">
                  {shoot.contactEmail}
                </a>
              </div>
              {shoot.contactPhone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${shoot.contactPhone}`} className="text-green-600 hover:underline">
                    {shoot.contactPhone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
            <h3 className="font-medium text-gray-900 mb-3">Quick Stats</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Industry</span>
                <span className="font-medium">{shoot.industry}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Company Size</span>
                <span className="font-medium capitalize">{shoot.companySize}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Priority</span>
                <span className={`font-medium ${
                  shoot.priority >= 8 ? 'text-red-600' :
                  shoot.priority >= 6 ? 'text-amber-600' :
                  'text-gray-600'
                }`}>{shoot.priority}/10</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="font-medium">{new Date(shoot.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Step Workspace */}
        <div className="lg:col-span-2">
          <StepWorkspace
            step={activeStepConfig}
            shoot={shoot}
            stepData={stepData[activeStepConfig.id] || { completed: false }}
            onSaveStepData={(data) => handleSaveStepData(activeStepConfig.id, data)}
            onCompleteStep={() => handleCompleteStep(activeStepConfig.step)}
          />
        </div>
      </div>
    </div>
  );
}